import { jsPDF } from 'jspdf';
import { buildQuestionarioSections, buildCheckFinaleSections } from './questionarioDisplay';

const PDF_MARGIN = 14;
const LINE_HEIGHT = 4.2;
const SECTION_SPACING = 7;
const COLUMN_GAP = 6;
const measurementUnits = {
  peso: 'kg',
  altezza: 'cm',
  fianchi: 'cm',
  addome: 'cm',
  torace: 'cm',
  coscia: 'cm',
  braccio: 'cm',
  massaMagra: 'kg',
  massaGrassa: 'kg',
  acquaCorporea: '%',
};

const QUESTIONARIO_PHOTO_SLOTS = [
  { key: 'anteriore', label: 'Frontale' },
  { key: 'posteriore', label: 'Posteriore' },
  { key: 'profiloSinistro', label: 'Profilo sinistro' },
  { key: 'profiloDestro', label: 'Profilo destro' },
];

const CHECK_PHOTO_SLOTS = [
  { key: 'anteriore', label: 'Frontale' },
  { key: 'posteriore', label: 'Posteriore' },
  { key: 'profiloSinistra', label: 'Profilo sinistro' },
  { key: 'profiloDestra', label: 'Profilo destro' },
];

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date?.getTime())) return '';
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createDocState = (doc) => ({
  margin: PDF_MARGIN,
  width: doc.internal.pageSize.getWidth() - PDF_MARGIN * 2,
  pageHeight: doc.internal.pageSize.getHeight(),
  cursorY: PDF_MARGIN,
  textColor: { primary: [33, 37, 41], secondary: [85, 93, 103] },
  borderColor: { light: [230, 233, 237], bold: [204, 209, 214] },
  accentFill: [245, 247, 250],
});

const ensureSpace = (doc, state, needed = 10) => {
  if (state.cursorY + needed <= state.pageHeight - state.margin) return;
  doc.addPage();
  state.cursorY = state.margin;
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value : String(value);
  return str.trim();
};

const buildUserLine = (meta = {}) => {
  const parts = [];
  if (meta.userLabel) parts.push(meta.userLabel);
  if (meta.userId) parts.push(`UID: ${meta.userId}`);
  return parts.join(' · ');
};

const buildProgramLine = (percorso = {}) => {
  const chips = [];
  const label = normalizeString(percorso.programTitle || percorso.programLabel);
  if (label) chips.push(label);
  if (percorso.productName) chips.push(percorso.productName);
  const seq =
    Number.isFinite(percorso.sequenceIndex) && Number.isFinite(percorso.sequenceTotal)
      ? `Step ${percorso.sequenceIndex + 1}/${percorso.sequenceTotal}`
      : null;
  if (seq) chips.push(seq);
  return chips.join(' · ');
};

const addDocumentHeader = (doc, state, { title, meta = {} }) => {
  ensureSpace(doc, state, 18);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...state.textColor.primary);
  doc.text((title || '').toUpperCase(), state.margin, state.cursorY);
  state.cursorY += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...state.textColor.secondary);
  const rows = [
    buildUserLine(meta),
    buildProgramLine(meta.percorso),
    meta.percorso?.plannedStartAt ? `Avvio previsto: ${formatDateLabel(meta.percorso.plannedStartAt)}` : '',
    meta.percorso?.expireAt ? `Scadenza programma: ${formatDateLabel(meta.percorso.expireAt)}` : '',
    meta.questionarioInfo?.updatedAt
      ? `Ultimo aggiornamento: ${formatDateLabel(meta.questionarioInfo.updatedAt)}`
      : '',
  ].filter(Boolean);
  rows.forEach((line) => {
    ensureSpace(doc, state, LINE_HEIGHT + 1);
    doc.text(line, state.margin, state.cursorY);
    state.cursorY += LINE_HEIGHT;
  });
  state.cursorY += 3;
  doc.setDrawColor(...state.borderColor.bold);
  doc.setLineWidth(0.2);
  doc.line(state.margin, state.cursorY, state.margin + state.width, state.cursorY);
  state.cursorY += 4;
};

const measurementUnitForEntry = (entry) => {
  if (!entry) return null;
  if (measurementUnits[entry.key]) return measurementUnits[entry.key];
  const root = entry.path?.[0];
  if (measurementUnits[root]) return measurementUnits[root];
  return null;
};

const formatEntryValue = (entry) => {
  if (!entry) return '—';
  const unit = measurementUnitForEntry(entry);
  if (unit) {
    const numeric = Number(entry.raw);
    if (Number.isFinite(numeric)) return `${numeric} ${unit}`;
  }
  return entry.display || '—';
};

const addSectionTitle = (doc, state, title) => {
  ensureSpace(doc, state, 12);
  doc.setFillColor(...state.accentFill);
  doc.rect(state.margin, state.cursorY, state.width, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...state.textColor.primary);
  doc.text(title.toUpperCase(), state.margin + 1, state.cursorY + 5);
  state.cursorY += 10;
  doc.setDrawColor(...state.borderColor.bold);
  doc.setLineWidth(0.2);
  doc.line(state.margin, state.cursorY, state.margin + state.width, state.cursorY);
  state.cursorY += 4;
  doc.setFontSize(9);
  doc.setTextColor(...state.textColor.secondary);
};

const addEntryBlock = (doc, state, entry) => {
  const value = formatEntryValue(entry);
  const lines = doc.splitTextToSize(value, state.width);
  const blockHeight = Math.max(LINE_HEIGHT * 2.4, lines.length * LINE_HEIGHT + 3);
  ensureSpace(doc, state, blockHeight + 4);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...state.textColor.primary);
  doc.text(entry.label, state.margin, state.cursorY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...state.textColor.secondary);
  doc.text(lines, state.margin, state.cursorY + LINE_HEIGHT);
  state.cursorY += blockHeight;
  doc.setDrawColor(...state.borderColor.light);
  doc.setLineWidth(0.15);
  doc.line(state.margin, state.cursorY, state.margin + state.width, state.cursorY);
  state.cursorY += 2;
};

const getPhotoUrl = (meta) => meta?.url || meta?.downloadURL || meta?.src || null;

const isImageMeta = (meta) => {
  const type = normalizeString(meta?.contentType);
  if (type && type.startsWith('image/')) return true;
  const name = normalizeString(meta?.name);
  if (!name) return false;
  return /\.(png|jpe?g|webp|gif|bmp)$/i.test(name);
};

const fetchDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Impossibile caricare la foto per il PDF:', error);
    return null;
  }
};

const detectImageFormat = (dataUrl) => {
  if (!dataUrl) return 'JPEG';
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  if (dataUrl.startsWith('data:image/gif')) return 'GIF';
  return 'JPEG';
};

const loadImageMeta = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 });
    img.onerror = reject;
    img.src = dataUrl;
  });

const preparePhotoAssets = async (photos = {}, slots = []) => {
  const images = [];
  const attachments = [];
  await Promise.all(
    slots.map(async (slot) => {
      const meta = photos[slot.key];
      if (!meta) return;
      const url = getPhotoUrl(meta);
      if (isImageMeta(meta) && url) {
        const dataUrl = await fetchDataUrl(url);
        if (dataUrl) {
          try {
            const dims = await loadImageMeta(dataUrl);
            images.push({ label: slot.label, dataUrl, ...dims });
          } catch (err) {
            console.warn('Impossibile leggere dimensioni foto PDF:', err);
          }
        }
      } else {
        attachments.push({
          label: slot.label,
          name: meta.name || slot.label,
          url,
        });
      }
    })
  );
  return { images, attachments };
};

const addPhotoGrid = (doc, state, images) => {
  if (!images.length) return;
  const columns = 2;
  const colWidth = (state.width - COLUMN_GAP) / columns;
  const maxHeight = colWidth * 1.05;

  for (let i = 0; i < images.length; i += columns) {
    const rowItems = images.slice(i, i + columns);
    ensureSpace(doc, state, maxHeight + 16);
    rowItems.forEach((item, index) => {
      const x = state.margin + index * (colWidth + COLUMN_GAP);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...state.textColor.primary);
      doc.text(item.label, x, state.cursorY);
      const aspect = (item.width || 1) / (item.height || 1);
      const width = Math.min(colWidth, maxHeight * aspect);
      const height = width / aspect;
      const y = state.cursorY + 2;
      doc.addImage(item.dataUrl, detectImageFormat(item.dataUrl), x, y, width, height, undefined, 'FAST');
    });
    state.cursorY += maxHeight + 10;
  }
};

const addPhotoAttachments = (doc, state, attachments) => {
  if (!attachments.length) return;
  ensureSpace(doc, state, attachments.length * LINE_HEIGHT + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...state.textColor.secondary);
  attachments.forEach((file) => {
    const label = file.url ? `${file.label}: ${file.name} (${file.url})` : `${file.label}: ${file.name}`;
    const lines = doc.splitTextToSize(label, state.width);
    doc.text(lines, state.margin, state.cursorY);
    state.cursorY += lines.length * LINE_HEIGHT;
  });
};

const addSectionEntries = async (doc, state, section, answers, photoSlots) => {
  addSectionTitle(doc, state, section.title);
  if (section.key === 'foto') {
    const { images, attachments } = await preparePhotoAssets(answers?.foto, photoSlots);
    if (!images.length && !attachments.length) {
      doc.setFont('helvetica', 'normal');
      doc.text('Nessuna foto caricata.', state.margin, state.cursorY);
      state.cursorY += LINE_HEIGHT + SECTION_SPACING;
      return;
    }
    addPhotoGrid(doc, state, images);
    addPhotoAttachments(doc, state, attachments);
    state.cursorY += SECTION_SPACING;
    return;
  }

  section.entries.forEach((entry) => addEntryBlock(doc, state, entry));
  state.cursorY += SECTION_SPACING;
};

const buildMeta = ({ userId, userLabel, questionarioInfo, percorso }) => ({
  userId: normalizeString(userId) || '',
  userLabel: normalizeString(userLabel) || '',
  questionarioInfo: questionarioInfo || {},
  percorso: percorso || {},
});

export const generateQuestionarioPdf = async ({ answers = {}, meta = {}, fileName } = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const state = createDocState(doc);
  addDocumentHeader(doc, state, { title: 'Questionario iniziale', meta: buildMeta(meta) });
  const sections = buildQuestionarioSections(answers);
  for (const section of sections) {
    // eslint-disable-next-line no-await-in-loop
    await addSectionEntries(doc, state, section, answers, QUESTIONARIO_PHOTO_SLOTS);
  }
  doc.save(fileName || 'questionario.pdf');
  return doc;
};

export const generateCheckFinalePdf = async ({ answers = {}, meta = {}, fileName } = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const state = createDocState(doc);
  addDocumentHeader(doc, state, { title: 'Check finale', meta: buildMeta(meta) });
  const sections = buildCheckFinaleSections(answers);
  for (const section of sections) {
    // eslint-disable-next-line no-await-in-loop
    await addSectionEntries(doc, state, section, answers, CHECK_PHOTO_SLOTS);
  }
  doc.save(fileName || 'check_finale.pdf');
  return doc;
};
